import { motion } from "motion/react";
import { ExternalLink, Mail, MapPin, Phone } from "lucide-react";
import { Button, Eyebrow, BookButton } from "../components/Buttons";
import { Stagger } from "../components/Reveal";
import { fadeUp, EASE } from "../motion";
import "./Contact.css";
import {
  ADDRESS,
  EMAIL,
  EMAIL_HREF,
  INSTAGRAM_URL,
  MAPS_URL,
  MOBILE,
  MOBILE_HREF,
  PHONE,
  PHONE_HREF,
  WEBSITE_URL,
  WEBSITE_LABEL,
} from "../config";

function InstagramIcon() {
  return (
    <svg
      className="icon-inline"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" />
    </svg>
  );
}

const row = {
  hidden: { opacity: 0, x: -14 },
  show: { opacity: 1, x: 0, transition: { duration: 0.5, ease: EASE } },
};

export default function Contact() {
  return (
    <section className="contact section--blue section" id="contact">
      <div className="container contact__inner">
        <Stagger className="contact__details" gap={0.08}>
          <motion.div variants={fadeUp}>
            <Eyebrow tone="on-blue">CONTACT</Eyebrow>
          </motion.div>
          <motion.h2 variants={fadeUp}>Visit us in Manly</motion.h2>
          <motion.p variants={fadeUp} className="contact__sub">
            Walk-ins welcome · Book online or by phone
          </motion.p>
          <motion.ul
            className="contact__list"
            variants={{ show: { transition: { staggerChildren: 0.07 } } }}
          >
            {/* Address → Google Maps */}
            <motion.li variants={row}>
              <MapPin />
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="contact__link"
              >
                {ADDRESS}
              </a>
            </motion.li>

            {/* Phone + Mobile → tap to call */}
            <motion.li variants={row}>
              <Phone />
              <span>
                <a href={PHONE_HREF} className="contact__link">
                  {PHONE}
                </a>
                {" · "}
                <a href={MOBILE_HREF} className="contact__link">
                  {MOBILE}
                </a>
              </span>
            </motion.li>

            {/* Email → opens mail app */}
            <motion.li variants={row}>
              <Mail />
              <a href={EMAIL_HREF} className="contact__link">
                {EMAIL}
              </a>
            </motion.li>

            <motion.li variants={row}>
              <InstagramIcon />
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="contact__link"
              >
                @numberonenailsmanly
              </a>
            </motion.li>

            {/* Website → new tab */}
            <motion.li variants={row}>
              <ExternalLink />
              <a
                href={WEBSITE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="contact__link"
              >
                {WEBSITE_LABEL}
              </a>
            </motion.li>
          </motion.ul>
          <motion.div variants={fadeUp} className="contact__ctas">
            <Button
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="white"
            >
              <MapPin />
              Open in Google Maps
            </Button>
          </motion.div>
        </Stagger>

        <motion.aside
          className="bookcard"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
        >
          <span className="bookcard__eyebrow">Online booking</span>
          <h3 className="bookcard__title">Book your appointment</h3>
          <p className="bookcard__text">
            Choose your service and time in under a minute.
          </p>
          <BookButton className="bookcard__btn">Book now</BookButton>
          <div className="bookcard__divider">
            <span>or call</span>
          </div>
          <a className="bookcard__phone" href={PHONE_HREF}>
            <Phone aria-hidden="true" />
            {PHONE}
          </a>
        </motion.aside>
      </div>
    </section>
  );
}
